import { ImpersonationKind } from "../model/user/types/ImpersonationKind.enum";

export const isImpersonation = () =>
  sessionStorage.getItem(process.env.REACT_APP_IMPERSONATION_FLAG_KEY!) === "1";

export const IMPERSONATION_KIND_KEY = "atira:imp-kind";

export const isSupportSession = () =>
  isImpersonation() &&
  sessionStorage.getItem(IMPERSONATION_KIND_KEY) === ImpersonationKind.SUPPORT;

export const PERSIST_ROOT_KEY = "persist:root";
export const PERSIST_IMPERSONATION_KEY = "persist:impersonation";

/**
 * Whether this tab is (or is about to become) an impersonation tab.
 *
 * The URL checks matter on the very first load of a freshly opened tab, before
 * the handshake in Impersonate.tsx has had a chance to set the flag. Must be
 * kept in agreement between the persistor config and the boot-time checks in
 * sessionBoot.ts, which is why it lives here rather than being inlined in both.
 */
export const isImpersonationTab = () => {
  const url = new URL(window.location.href);

  return (
    url.pathname.startsWith("/impersonate") ||
    url.searchParams.get("imp") === "1" ||
    isImpersonation()
  );
};

/**
 * Keyed off isImpersonationTab() rather than the flag on purpose: between the
 * moment an impersonation tab loads and the moment the handshake sets the flag,
 * a flag-based check reads false and would hand the tab the *main* account's
 * token out of shared localStorage. An impersonation tab must never read - or,
 * on logout, clear - the main account's credentials, even when its own
 * handshake never completes.
 */
export const getToken = () => {
  return isImpersonationTab()
    ? sessionStorage.getItem(
        process.env.REACT_APP_LOCAL_STORAGE_TOKEN_IMPERSONATION_KEY!,
      )
    : localStorage.getItem(process.env.REACT_APP_LOCAL_STORAGE_TOKEN_KEY!);
};

/**
 * Reads the `userId` claim out of a JWT without verifying it.
 *
 * Verification is the API's job - we only need the claim to check that the
 * token we are about to send describes the same account the UI is currently
 * rendering. Returns undefined for anything unparseable.
 */
export const getTokenUserId = (token?: string | null): string | undefined => {
  const raw = token === undefined ? getToken() : token;

  if (!raw) {
    return undefined;
  }

  try {
    const payload = raw.split(".")[1];

    if (!payload) {
      return undefined;
    }

    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = decodeURIComponent(
      atob(normalized)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(""),
    );

    return JSON.parse(decoded)?.userId;
  } catch {
    return undefined;
  }
};

type StoreRef = { getState: () => any };

let storeRef: StoreRef | null = null;

export const setStoreRef = (store: StoreRef) => {
  storeRef = store;
};

export const getStateLoggedInUserId = (): string | undefined =>
  storeRef?.getState()?.user?.loggedInUserId;
